<script setup lang="ts">
import { computed, reactive, ref, toRaw } from 'vue';
import { watchDebounced } from '@vueuse/core';
import _isEqual from 'lodash/isEqual';

import KMultipleSelectField from '@/components/fields/KMultipleSelectField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import DispatchMoreFiltersFieldset from '@/components/fieldsets/DispatchMoreFiltersFieldset.vue';
import DatePickerField from '@/components/pickerFields/DatePickerField.vue';
import useForm from '@/composables/useForm';
import { useStaticStore } from '@/stores/useStaticStore';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  search?: string | null;
  serviceAreas?: string[] | null;
  serviceGroups?: string[] | null;
  moreFilters: {
    sites?: string[] | null;
    goods?: string[] | null;
    switches: {
      isCanSchedule?: boolean;
      isPayOnHold?: boolean;
      isInvoice?: boolean;
    };
  };
  date?: string | null;
}

export interface ValidData {
  search?: string | null;
  serviceAreas?: string[] | null;
  serviceGroups?: string[] | null;
  moreFilters: {
    sites?: string[] | null;
    goods?: string[] | null;
    switches: {
      isCanSchedule?: boolean;
      isPayOnHold?: boolean;
      isInvoice?: boolean;
    };
  };
  date: string;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({
    moreFilters: {
      switches: {},
    },
  }),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({
  moreFilters: {
    switches: {},
  },
});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  emit('submit', toRaw(formData) as ValidData);
});

watchDebounced(
  [
    () => formData.search,
    () => formData.serviceAreas,
    () => formData.serviceGroups,
    () => formData.moreFilters.sites,
    () => formData.moreFilters.goods,
    () => formData.moreFilters.switches.isCanSchedule,
    () => formData.moreFilters.switches.isPayOnHold,
    () => formData.moreFilters.switches.isInvoice,
    () => formData.date,
  ],
  async (newValues, oldValues) => {
    if (!_isEqual(newValues, oldValues)) {
      await handleSubmit();
    }
  },
  { deep: true, debounce: 500 },
);

const staticStore = useStaticStore();

const dispatchServiceAreasItems = computed(() => {
  return staticStore.getServiceAreas
    .filter((serviceArea) => {
      return !!serviceArea.description;
    })
    .map((serviceArea) => {
      return {
        // TODO as string
        value: serviceArea.description as string,
        // TODO as string
        title: serviceArea.description as string,
      };
    });
});

const dispatchServiceGroupsItems = computed(() => {
  return staticStore.getServiceGroups
    .filter((serviceGroup) => {
      return !!serviceGroup.description;
    })
    .map((serviceGroup) => {
      return {
        value: serviceGroup.id,
        title: serviceGroup.id,
      };
    });
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.dispatchJobSearchForm"
    data-testid="dispatchJobSearchForm"
    @submit.prevent="handleSubmit"
  >
    <div :class="$style.leftColumn">
      <KTextField
        v-model="formData.search"
        label="Search"
        :class="$style.field"
        placeholder="Type to search..."
        clearable
        hide-details
      />
    </div>
    <div :class="$style.rightColumn">
      <KMultipleSelectField
        v-model="formData.serviceAreas"
        label="Service Areas"
        :class="[$style.field, $style.serviceAreasSelectField]"
        :items="dispatchServiceAreasItems"
        :plusCount="1"
        hide-details
        @keydown.enter="handleSubmit"
      />
      <KMultipleSelectField
        v-model="formData.serviceGroups"
        label="Services"
        :class="[$style.field, $style.serviceGroupsSelectField]"
        :items="dispatchServiceGroupsItems"
        :plusCount="1"
        hide-details
        @keydown.enter="handleSubmit"
      />
      <DispatchMoreFiltersFieldset
        v-model="formData.moreFilters"
        :class="$style.moreFiltersFieldset"
      />
      <DatePickerField
        v-model="formData.date"
        label="Date"
        :rules="[requiredValidationRule]"
        :class="[$style.field, $style.datePickerField]"
        hide-details
      />
    </div>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchJobSearchForm {
  display: flex;
}
  .leftColumn {
    margin-right: auto;
  }
  .rightColumn {
    text-align: right;

    .field {
      margin-left: 1.5rem;
    }
  }
    .field {
      width: 180px;
      display: inline-block;
      vertical-align: top;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    .serviceAreasSelectField {
      width: 205px;
    }
    .serviceGroupsSelectField {
      width: 160px;
    }
    .moreFiltersFieldset {
      display: inline-block;
      margin-left: 1.5rem;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    .datePickerField {
      width: 180px;
    }
</style>
